import { HTTPAidaBaseService } from "adapters/http";
import {
  fixAndParseJsonFromGPT,
  generateResponseForPrompt,
  generateResponseLanchainHelper,
} from "config/helpers/common_helper";
import jwtDecode from "jwt-decode";
import * as SpeechSDK from "microsoft-cognitiveservices-speech-sdk";
import { useEffect, useState, useRef } from "react";
import * as AudioUtils from "../components/SpeechToText/AudioUtils";
import XMLParser from "react-xml-parser";
import axios from "axios";
import { ChatOpenAI } from "@langchain/openai";
import { Howl, Howler } from "howler";

const usePlayAIAgent = ({
  speechToTextService = true,
  textToSpeechService = false,
  tokenRefreshInterval = 57000,
  audioProcessing = false,
} = {}) => {
  const [audioStatus, setAudioStatus] = useState({
    isPlaying: false,
    isPaused: false,
    isStopped: true,
  });

  const [transcripts, setTranscripts] = useState([]);

  const recognizerRef = useRef(null);

  const pushStreamRef = useRef(null);
  const audioContextRef = useRef(null);

  const workletNodeRef = useRef(null);
  const pauseStreamingRef = useRef(false);

  const streamRef = useRef(null);
  const bufferRef = useRef([]);
  const websocketRef = useRef(null);

  const audioQueueRef = useRef([]);
  const howlRef = useRef(null);

  const sampleRateRef = useRef(22050);
  const playbackAudioContext = useRef(null);

  const audioBufferQueueRef = useRef([]);
  const sourceRef = useRef(null);
  const isPlayingRef = useRef(false);

  const agentId = `Noki-Virtual-Ca-svYLvoaVANDP6liZoHOBT`;
  const apiKey = `ak-2b63367fcf8646c085e27e7e615278e7`;

  useEffect(() => {
    playbackAudioContext.current = new AudioContext();
    audioContextRef.current = new AudioContext();
  }, []);

  //   const resampleAudioBuffer = async (buffer, fromSampleRate, toSampleRate) => {
  //     if (fromSampleRate === toSampleRate) {
  //       return buffer;
  //     }

  //     const offlineContext = new OfflineAudioContext(
  //       1,
  //       (buffer.length * toSampleRate) / fromSampleRate,
  //       toSampleRate
  //     );
  //     const audioBuffer = offlineContext.createBuffer(
  //       1,
  //       buffer.length,
  //       fromSampleRate
  //     );
  //     audioBuffer.copyToChannel(buffer, 0);

  //     const source = offlineContext.createBufferSource();
  //     source.buffer = audioBuffer;
  //     source.connect(offlineContext.destination);
  //     source.start();

  //     const renderedBuffer = await offlineContext.startRendering();
  //     return renderedBuffer.getChannelData(0);
  //   };

  //   useEffect(() => {
  //     const playAudio = async () => {
  //       while (true) {
  //         if (bufferRef.current.length === 0) {
  //           if (
  //             audioQueueRef.current.length >=
  //             playbackAudioContext.current.sampleRate / 2
  //           ) {
  //             bufferRef.current = audioQueueRef.current.splice(
  //               0,
  //               playbackAudioContext.current.sampleRate / 2
  //             );
  //           } else {
  //             await new Promise((resolve) => setTimeout(resolve, 100));
  //             continue;
  //           }
  //         }

  //         const resampledData = await resampleAudioBuffer(
  //           new Float32Array(bufferRef.current),
  //           sampleRateRef.current,
  //           playbackAudioContext.current.sampleRate
  //         );

  //         const audioBuffer = playbackAudioContext.current.createBuffer(
  //           1,
  //           resampledData.length,
  //           playbackAudioContext.current.sampleRate
  //         );
  //         audioBuffer.copyToChannel(resampledData, 0);
  //         bufferRef.current = [];

  //         const source = playbackAudioContext.current.createBufferSource();
  //         source.buffer = audioBuffer;
  //         source.connect(playbackAudioContext.current.destination);
  //         source.start();
  //         await new Promise((resolve) => (source.onended = resolve));
  //       }
  //     };

  //     playAudio();
  //   }, []);

  const base64ToFloat32Array = (base64) => {
    const binaryString = atob(base64);
    const len = binaryString.length;
    const bytes = new Uint8Array(len);
    for (let i = 0; i < len; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }
    return new Float32Array(bytes.buffer);
  };

  const connectToServer = () => {
    websocketRef.current = new WebSocket(
      "wss://api.play.ai/v1/talk/" + agentId
    );
    websocketRef.current.onopen = () => {
      console.log("connected!");
      websocketRef.current.send(
        JSON.stringify({
          type: "setup",
          apiKey: apiKey,
          inputEncoding: "linear16",
          inputSampleRate: 16000,
          outputFormat: "raw",
        })
      );
    };

    const stopAudio = () => {
      if (sourceRef.current) {
        sourceRef.current.stop();
        sourceRef.current.disconnect();
        sourceRef.current = null;
        isPlayingRef.current = false;
      }
      audioBufferQueueRef.current = [];
    };

    const playNextInQueue = () => {
      if (audioBufferQueueRef.current.length === 0) {
        isPlayingRef.current = false;
        return;
      }

      isPlayingRef.current = true;
      const audioBuffer = audioBufferQueueRef.current.shift();

      sourceRef.current = playbackAudioContext.current.createBufferSource();
      sourceRef.current.buffer = audioBuffer;
      sourceRef.current.connect(playbackAudioContext.current.destination);
      sourceRef.current.start();
      sourceRef.current.playbackRate.value = 0.55;

      sourceRef.current.onended = () => {
        playNextInQueue();
      };
    };

    const playAudioData = async (base64Data) => {
      const binaryString = atob(base64Data);
      const len = binaryString.length;
      const bytes = new Uint8Array(len);

      for (let i = 0; i < len; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }

      // Convert PCM float32 data to AudioBuffer
      const audioBuffer = playbackAudioContext.current.createBuffer(
        1,
        bytes.length / 4,
        44100
      );
      const float32Array = new Float32Array(bytes.buffer);

      audioBuffer.copyToChannel(float32Array, 0);

      audioBufferQueueRef.current.push(audioBuffer);

      if (!isPlayingRef.current) {
        playNextInQueue();
      }
    };

    websocketRef.current.onmessage = async (message) => {
      const event = JSON.parse(message.data);
      if (event.type === "audioStream") {
        await playAudioData(event.data);
      } else if (event.type === "voiceActivityStart") {
        stopAudio();
      } else if (event.type === "voiceActivityEnd") {
        // Handle voice activity end if necessary
      } else if (event.type === "newAudioStream") {
        stopAudio();
      }
      //   if (event.type === "audioStream") {

      //     const base64Data = event.data;
      //     const float32Array = base64ToFloat32Array(base64Data);
      //     audioQueueRef.current.push(...float32Array);
      //   }
    };
  };

  const createAzureSTTProcessorNode = async () => {
    if (!window.AudioWorkletNode) {
      console.error("AudioWorkletNode is not supported in this browser");
      return;
    }

    if (!audioContextRef.current) {
      try {
        audioContextRef.current = new AudioContext();
      } catch (e) {
        console.error(`Error creating audio context`, e);
        return null;
      }
    }

    let processorNode = null;
    try {
      processorNode = new AudioWorkletNode(
        audioContextRef.current,
        "audio-process"
      );
    } catch (e) {
      console.error(`Adding Processor`, e);
      try {
        await audioContextRef.current.audioWorklet.addModule(
          "audio-process.js"
        );
        processorNode = new AudioWorkletNode(
          audioContextRef.current,
          "audio-process"
        );
      } catch (e) {
        console.log(`** Error: Unable to create worklet node: ${e}`);
        return null;
      }
    }

    await audioContextRef.current.resume();
    return processorNode;
  };

  function arrayBufferToBase64(buffer) {
    let binary = "";
    const bytes = new Uint8Array(buffer);
    const len = bytes.byteLength;

    for (let i = 0; i < len; i++) {
      binary += String.fromCharCode(bytes[i]);
    }

    return window.btoa(binary);
  }

  const convertBrowserStreamToAudioStreamUsingWorklet = async (stream) => {
    try {
      workletNodeRef.current = await createAzureSTTProcessorNode();
      const source = audioContextRef.current.createMediaStreamSource(stream);

      console.log(`source details`, source);

      const highPassFilter = audioContextRef.current.createBiquadFilter();
      highPassFilter.type = "highpass";
      highPassFilter.frequency.setValueAtTime(
        300,
        audioContextRef.current.currentTime
      );
      highPassFilter.Q.setValueAtTime(1, audioContextRef.current.currentTime);

      const lowPassFilter = audioContextRef.current.createBiquadFilter();
      lowPassFilter.type = "lowpass";
      lowPassFilter.frequency.setValueAtTime(
        3400,
        audioContextRef.current.currentTime
      );
      lowPassFilter.Q.setValueAtTime(1, audioContextRef.current.currentTime);

      const compressor = audioContextRef.current.createDynamicsCompressor();
      compressor.threshold.setValueAtTime(
        -60,
        audioContextRef.current.currentTime
      );
      compressor.knee.setValueAtTime(30, audioContextRef.current.currentTime);
      compressor.ratio.setValueAtTime(12, audioContextRef.current.currentTime);
      compressor.attack.setValueAtTime(
        0.01,
        audioContextRef.current.currentTime
      );
      compressor.release.setValueAtTime(
        0.3,
        audioContextRef.current.currentTime
      );

      workletNodeRef.current.parameters
        .get("desiredSampleRate")
        .setValueAtTime(16000, audioContextRef.current.currentTime);

      workletNodeRef.current.parameters
        .get("encoded16bit")
        .setValueAtTime(1, audioContextRef.current.currentTime);

      workletNodeRef.current.parameters
        .get("bufferSize")
        .setValueAtTime(3072, audioContextRef.current.currentTime);

      workletNodeRef.current.parameters
        .get("noiseGate")
        .setValueAtTime(
          audioProcessing ? 1 : 0,
          audioContextRef.current.currentTime
        );

      if (audioProcessing) {
        source.connect(highPassFilter);
        highPassFilter.connect(lowPassFilter);
        lowPassFilter.connect(workletNodeRef.current);
      } else {
        source.connect(workletNodeRef.current);
      }

      workletNodeRef.current.port.onmessage = (event) => {
        const pcmEncodedData = event.data;
        let base64String = arrayBufferToBase64(pcmEncodedData);
        if (websocketRef.current?.readyState !== WebSocket.OPEN) return;
        websocketRef.current.send(
          JSON.stringify({
            type: "audioIn",
            data: base64String,
          })
        );
      };
    } catch (e) {
      console.error(e);
    }
  };

  const startStreaming = async () => {
    try {
      connectToServer();
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
      });

      streamRef.current = stream;

      convertBrowserStreamToAudioStreamUsingWorklet(stream);
    } catch (e) {
      console.error(e);
    }
  };

  const resumeStreaming = () => {
    pauseStreamingRef.current = false;
    setAudioStatus((prev) => ({
      ...prev,
      isStopped: false,
      isPlaying: true,
    }));
  };

  const pauseStreaming = () => {
    pauseStreamingRef.current = true;
    setAudioStatus((prev) => ({
      ...prev,
      isStopped: true,
      isPlaying: false,
    }));
  };

  const stopStreams = () => {
    if (audioContextRef.current) {
      audioContextRef.current.close();
      audioContextRef.current = null;
    }

    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => {
        track.stop();
      });

      streamRef.current = null;
    }
  };

  const toggleTranscription = async () => {
    startStreaming();
    // if (audioStatus?.isStopped) {
    //   startStreaming();
    // } else {
    //   pauseStreaming();
    // }
  };

  const stopTranscription = async () => {
    stopStreams();
  };

  return {
    toggleTranscription,
    stopTranscription,
    transcripts,
    audioStatus,
    connectToServer,
  };
};

export default usePlayAIAgent;
